/**
 * Color By Legend Content
 * Displays the pinned vessel color legend
 */

import React, { useMemo } from 'react';
import { cn } from '@ais-ui/utils';
import { useMapLegendColors } from '@ais-ui/websockets';
import { useSettings } from '../../../Settings/SettingsProvider';
import { useLayers } from '@ais-ui/react-openlayers';
import { useMapLegendSettings } from '../../ColorTargetsBy/ColorBy/useMapLegendSettings';
import { PiEyeSlash } from 'react-icons/pi';

interface LegendItemProps {
  color: string | null;
  name: string;
  isHidden: boolean;
  onClick: () => void;
}

const LegendItem: React.FC<LegendItemProps> = ({
  color,
  name,
  isHidden,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex items-center gap-2 min-w-[80px] text-left hover:bg-muted/50 rounded px-1 py-0.5 -mx-1 transition-colors',
      isHidden && 'opacity-50',
    )}
  >
    <div
      className="h-3 w-3 rounded-sm border border-border shrink-0"
      style={{ backgroundColor: color || '#888' }}
    />
    <span className="text-xs truncate flex-1">{name}</span>
    {isHidden && (
      <PiEyeSlash className="size-3 text-muted-foreground shrink-0" />
    )}
  </button>
);

export const ColorByLegendContent: React.FC = () => {
  const { settings } = useSettings();
  const { data: legendColors } = useMapLegendColors();
  const { layers, toggleLayerVisibility } = useLayers();
  const { onActivate } = useMapLegendSettings();

  const colorState = settings['ribbonPlugin.settings']?.['ship_color_state'];
  const notShownLegendItems = useMemo(() => {
    return settings['ribbonPlugin.settings']?.['ship_color_noshow_sel'] || [];
  }, [settings]);

  const shipLayer = useMemo(
    () => layers.find((l) => l.name.toLowerCase() === 'vessels'),
    [layers],
  );

  const handleToggleItem = (name: string) => {
    const isCurrentlyHidden = notShownLegendItems.includes(name);

    // If showing an item and the ship layer is hidden, show it
    if (isCurrentlyHidden && shipLayer && !shipLayer.isVisible) {
      toggleLayerVisibility(shipLayer.layer, true);
    }

    onActivate(!isCurrentlyHidden, name, undefined);
  };

  // Get display name for color state
  const colorStateName = useMemo(() => {
    switch (colorState) {
      case 'LegendShipType':
        return 'Ship Type';
      case 'LegendSourceType':
        return 'Source Type';
      case 'LegendTransponderType':
        return 'Transponder Type';
      case 'LegendShipList':
        return 'Ship List';
      case 'LegendAge':
        return 'Target Age';
      default:
        return 'Color';
    }
  }, [colorState]);

  // Don't show if no color state or no colors
  if (
    !colorState ||
    colorState === 'no colors' ||
    !legendColors ||
    legendColors.length === 0
  ) {
    return (
      <div className="text-xs text-muted-foreground">
        No color categorization active
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground mb-2">
        By {colorStateName}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {legendColors.map((item, index) => (
          <LegendItem
            key={index}
            color={item.color}
            name={item.name}
            isHidden={notShownLegendItems.includes(item.name)}
            onClick={() => handleToggleItem(item.name)}
          />
        ))}
      </div>
    </div>
  );
};
